import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { getStaffWithBookingStatus, User } from '../../../../services/api';
import { formatRoles } from '../../../../utils/roleUtils';
import {
  BOOKING_STATUS,
  BOOKING_STATUS_LABELS,
  BOOKING_STATUS_COLORS,
  COLORS,
} from '../../../../constants/booking';
import StaffSummaryCards from './StaffSummaryCards';

const StaffLiveTrackingTab: React.FC = () => {
  const [allStaff, setAllStaff] = useState<User[]>([]);
  const [staffStats, setStaffStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0,
    onDuty: 0,
    available: 0,
  });
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState<User | null>(null);
  const [statusFilters, setStatusFilters] = useState<string[]>(['active', 'inactive', 'suspended']);
  const [dutyStatusFilters, setDutyStatusFilters] = useState<string[]>(['on_duty', 'available']);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchStaffData();
    fetchStaffStats();
    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchStaffData();
      fetchStaffStats();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, statusFilters, dutyStatusFilters]);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      // Use optimized endpoint with server-side filtering
      const statusParam = statusFilters.length > 0 && statusFilters.length < 3 
        ? statusFilters.join(',')
        : undefined;
      
      const dutyStatusParam = dutyStatusFilters.length > 0 && dutyStatusFilters.length < 2
        ? dutyStatusFilters.join(',')
        : undefined;

      const response = await getStaffWithBookingStatus(
        1,
        1000, // Fetch up to 1000 for now (can be paginated later)
        debouncedSearchTerm || undefined,
        statusParam,
        dutyStatusParam
      );
      setAllStaff(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load staff data');
      console.error('Failed to load staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffStats = async () => {
    try {
      // Fetch stats without filters to get accurate totals
      const response = await getStaffWithBookingStatus(1, 1); // Just to get total count
      const totalCount = response.data.pagination?.totalItems || 0;
      
      // Fetch counts by status
      const [activeRes, inactiveRes, suspendedRes, onDutyRes] = await Promise.all([
        getStaffWithBookingStatus(1, 1, undefined, 'active'),
        getStaffWithBookingStatus(1, 1, undefined, 'inactive'),
        getStaffWithBookingStatus(1, 1, undefined, 'suspended'),
        getStaffWithBookingStatus(1, 1, undefined, undefined, 'on_duty'),
      ]);

      const active = activeRes.data.pagination?.totalItems || 0;
      const inactive = inactiveRes.data.pagination?.totalItems || 0;
      const suspended = suspendedRes.data.pagination?.totalItems || 0;
      const onDuty = onDutyRes.data.pagination?.totalItems || 0;
      const available = active - onDuty;

      setStaffStats({
        total: totalCount,
        active,
        inactive,
        suspended,
        onDuty,
        available,
      });
    } catch (err: any) {
      console.error('Failed to load staff stats:', err);
    }
  };

  // Create booking map from staff data (already includes booking info from optimized endpoint)
  const bookingMap = React.useMemo(() => {
    const map = new Map<string, { status: string; pickupLocation?: { latitude: number; longitude: number } }>();
    allStaff.forEach((staff) => {
      if (staff.spec?.activeBooking) {
        map.set(staff._id || '', {
          status: staff.spec.activeBooking.status,
          pickupLocation: staff.spec.activeBooking.pickupLocation,
        });
      }
    });
    return map;
  }, [allStaff]);

  // Get running status for a staff member based on their active booking
  const getStaffRunningStatus = React.useCallback((staffId: string): { status: string; color: string } | null => {
    const booking = bookingMap.get(staffId);
    if (!booking) return null;

    const statusMap: Record<string, { status: string; color: string }> = {
      [BOOKING_STATUS.PENDING]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.PENDING], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PENDING] },
      [BOOKING_STATUS.ASSIGNED]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.ASSIGNED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.ASSIGNED] },
      [BOOKING_STATUS.IN_TRANSIT]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.IN_TRANSIT], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.IN_TRANSIT] },
      [BOOKING_STATUS.PATIENT_PICKED]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_PICKED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_PICKED] },
      [BOOKING_STATUS.PATIENT_DROPPED]: { status: BOOKING_STATUS_LABELS[BOOKING_STATUS.PATIENT_DROPPED], color: BOOKING_STATUS_COLORS[BOOKING_STATUS.PATIENT_DROPPED] },
    };

    return statusMap[booking.status] || null;
  }, [bookingMap]);

  // Staff list is already filtered by server, no need for additional client-side filtering
  const filteredStaffForList = allStaff;

  // Handle staff click
  const handleStaffClick = (staff: User) => {
    // Toggle selection
    if (selectedStaff?._id === staff._id) {
      setSelectedStaff(null);
    } else {
      setSelectedStaff(staff);
    }
  };

  // Get staff with locations for map (location is already included from optimized endpoint)
  const staffWithLocations = React.useMemo(() => {
    return filteredStaffForList
      .filter((staff) => staff.spec?.location)
      .map((staff) => ({
        staff,
        location: staff.spec!.location!,
      }));
  }, [filteredStaffForList]);

      return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
      {/* Summary Cards */}
      <StaffSummaryCards staffStats={staffStats} />

      {/* Main Content Area */}
      <div className="tracking-content" style={{ display: 'flex', gap: '16px', height: '100%', flex: 1 }}>
        {/* Left Sidebar - Staff List */}
      <div
        style={{
            width: '300px',
            minWidth: '300px',
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
          borderRadius: '8px',
            padding: '16px',
            border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
          display: 'flex',
          flexDirection: 'column',
            height: 'fit-content',
            maxHeight: 'calc(100vh - 200px)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
              }}
            >
              Staff
        </h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '4px 8px',
                border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
                borderRadius: '4px',
                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s',
              }}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>

        {/* Search Input */}
          <input
            type="text"
            placeholder="Search by name, email, phone, role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '220px',
              flex: '0 0 220px',
              padding: '8px 12px',
              marginBottom: '12px',
              border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
              borderRadius: '6px',
              backgroundColor: isDark ? '#0f172a' : '#ffffff',
              color: isDark ? '#f1f5f9' : '#0f172a',
              fontSize: '16px',
            }}
          />

          {/* Filters Section */}
          {showFilters && (
            <>
              {/* Status Filter */}
              <div style={{ marginBottom: '12px' }}>
                <div
              style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_MUTED : COLORS.THEME.LIGHT.TEXT_MUTED,
                    marginBottom: '8px',
                  }}
                >
                  Status
                </div>
                {[
                  { value: 'active', label: 'Active', color: '#10b981' },
                  { value: 'inactive', label: 'Inactive', color: '#6b7280' },
                  { value: 'suspended', label: 'Suspended', color: '#f59e0b' },
                ].map((status) => {
                  const isChecked = statusFilters.includes(status.value);
                  return (
                    <label
                      key={status.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '6px',
                cursor: 'pointer',
                        fontSize: '15px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            setStatusFilters(statusFilters.filter((s) => s !== status.value));
                          } else {
                            setStatusFilters([...statusFilters, status.value]);
                          }
                        }}
                        style={{
                          accentColor: status.color,
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: status.color,
                          }}
                        />
                        <span>{status.label}</span>
        </div>
                    </label>
                  );
                })}
          </div>

              {/* Duty Status Filter */}
              <div style={{ marginBottom: '12px' }}>
        <div
          style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: isDark ? COLORS.THEME.DARK.TEXT_MUTED : COLORS.THEME.LIGHT.TEXT_MUTED,
                    marginBottom: '8px',
                  }}
                >
                  Duty Status
                </div>
                {[
                  { value: 'on_duty', label: 'On Duty', color: '#ef4444' },
                  { value: 'available', label: 'Available', color: '#10b981' },
                ].map((status) => {
                  const isChecked = dutyStatusFilters.includes(status.value);
                  return (
                    <label
                      key={status.value}
                      style={{
            display: 'flex',
                        alignItems: 'center',
            gap: '8px',
                        marginBottom: '6px',
                        cursor: 'pointer',
                        fontSize: '15px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            setDutyStatusFilters(dutyStatusFilters.filter((s) => s !== status.value));
                          } else {
                            setDutyStatusFilters([...dutyStatusFilters, status.value]);
                          }
                        }}
                        style={{
                          accentColor: status.color,
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: status.color,
                          }}
                        />
                        <span>{status.label}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {/* Count Display */}
          {!loading && (
            <div
              style={{
                fontSize: '14px',
                color: isDark ? COLORS.THEME.DARK.TEXT_SECONDARY : COLORS.THEME.LIGHT.TEXT_SECONDARY,
                marginBottom: '12px',
                padding: '8px',
                backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                borderRadius: '4px',
              }}
            >
              {filteredStaffForList.length} staff member{filteredStaffForList.length !== 1 ? 's' : ''}
              {debouncedSearchTerm.trim() && (
                <span style={{ display: 'block', fontSize: '13px', marginTop: '4px' }}>
                  Search: "{debouncedSearchTerm}"
                </span>
              )}
            </div>
          )}

          {/* Staff List */}
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#94a3b8' : '#64748b' }}>
              Loading staff...
            </div>
          ) : filteredStaffForList.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#94a3b8' : '#64748b' }}>
              {statusFilters.length < 3 || dutyStatusFilters.length < 2 || debouncedSearchTerm.trim()
                ? 'No staff match the current filters'
                : 'No staff found'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
              {filteredStaffForList.map((staff) => {
                const isSelected = selectedStaff?._id === staff._id;
                const runningStatus = getStaffRunningStatus(staff._id || '');
                const isOnDuty = staff.spec?.isOnDuty === true;
                const primaryRole = (staff.spec?.roles || [])[0] || 'Staff';

              return (
                <div
                    key={staff._id}
                    onClick={() => handleStaffClick(staff)}
                  style={{
                    padding: '12px',
                    borderRadius: '6px',
                      border: `1px solid ${isSelected ? '#3b82f6' : isDark ? '#334155' : '#e2e8f0'}`,
                      backgroundColor: isSelected
                        ? isDark
                          ? '#1e3a8a'
                          : '#dbeafe'
                        : isDark
                        ? '#0f172a'
                        : '#f8fafc',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.backgroundColor = isDark ? '#0f172a' : '#f1f5f9';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.backgroundColor = isDark ? '#0f172a' : '#f8fafc';
                    }
                  }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                    <div
                      style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                            marginBottom: '4px',
                          }}
                        >
                          {staff.metadata?.name || 'Unknown'}
                  </div>
                        {runningStatus && (
      <div
        style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '13px',
                  fontWeight: '500',
                              backgroundColor: `${runningStatus.color}20`,
                              color: runningStatus.color,
                              marginBottom: '4px',
                            }}
                          >
                            {runningStatus.status}
            </div>
          )}
        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {primaryRole && (
                        <span
                style={{
                            fontSize: '13px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: isDark ? '#334155' : '#e2e8f0',
                            color: isDark ? COLORS.THEME.DARK.TEXT_MUTED : COLORS.THEME.LIGHT.TEXT_MUTED,
                          }}
                        >
                          {primaryRole}
                        </span>
                      )}
                      {staff.spec?.status && (
                        <span
                style={{
                            fontSize: '13px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor:
                              staff.spec.status === 'active'
                                ? '#10b981'
                                : staff.spec.status === 'suspended'
                                ? '#f59e0b'
                                : isDark
                                ? '#334155'
                                : '#e2e8f0',
                            color: staff.spec.status === 'active' ? '#ffffff' : isDark ? '#cbd5e1' : '#475569',
                          }}
                        >
                          {staff.spec.status}
                        </span>
                      )}
                      {isOnDuty && (
                        <span
            style={{
                            fontSize: '13px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                          }}
                        >
                          On Duty
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        </div>

        {/* Main Map Area */}
        <div className="tracking-main" style={{ flex: 1 }}>
          <div className="tracking-map-section">
            <div className="tracking-map-container">
              <StaffMap
                staff={filteredStaffForList}
                staffWithLocations={staffWithLocations}
                selectedStaff={selectedStaff}
                bookingMap={bookingMap}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const StaffMap: React.FC<{
  staff: User[];
  staffWithLocations: Array<{ staff: User; location: { latitude: number; longitude: number } }>;
  selectedStaff: User | null;
  bookingMap: Map<string, { status: string; pickupLocation?: { latitude: number; longitude: number } }>;
}> = ({ staffWithLocations, selectedStaff, bookingMap }) => {
  const [mapMode, setMapMode] = useState<'standard' | 'satellite'>('standard');
  const mapRef = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [infoWindows, setInfoWindows] = useState<google.maps.InfoWindow[]>([]);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const closeInfoWindow = React.useCallback((infoWindow: google.maps.InfoWindow) => {
    if (infoWindow && 'close' in infoWindow) {
      (infoWindow as any).close();
    }
  }, []);

  // Filter to show only selected staff when one is selected
  const displayStaff = React.useMemo(() => {
    if (selectedStaff) {
      return staffWithLocations.filter((item) => item.staff._id === selectedStaff._id);
    }
    return staffWithLocations;
  }, [staffWithLocations, selectedStaff]);

  // Calculate center from staff locations
  const calculateCenter = React.useMemo((): [number, number] => {
    if (displayStaff.length === 0) {
      return [28.6139, 77.2090]; // Default to Delhi, India
    }
    
    const avgLat = displayStaff.reduce((sum, item) => sum + item.location.latitude, 0) / displayStaff.length;
    const avgLng = displayStaff.reduce((sum, item) => sum + item.location.longitude, 0) / displayStaff.length;
    
    return [avgLat, avgLng];
  }, [displayStaff]);

  const defaultCenter = calculateCenter;
  const defaultZoom = displayStaff.length === 1 ? 15 : displayStaff.length > 1 ? 12 : 10;

  // Create custom staff icon
  const createStaffIcon = (isOnDuty: boolean): google.maps.Icon => {
    const color = isOnDuty ? '#ef4444' : '#10b981';
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="20" fill="${color}" stroke="#FFFFFF" stroke-width="4" opacity="1"/>
          <text x="24" y="32" font-size="30" text-anchor="middle" font-family="Arial, sans-serif">👤</text>
        </svg>
      `),
      scaledSize: new google.maps.Size(48, 48),
      anchor: new google.maps.Point(24, 24),
    };
  };

  // Initialize Google Map
  const initializeMap = React.useCallback(() => {
    if (!mapRef.current || !window.google || !window.google.maps) return;

    const center = new google.maps.LatLng(defaultCenter[0], defaultCenter[1]);

    const googleMap = new google.maps.Map(mapRef.current, {
      center,
      zoom: defaultZoom,
      mapTypeId: mapMode === 'satellite' ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP,
      fullscreenControl: true,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
    });

    setMap(googleMap);
  }, [defaultCenter, defaultZoom, mapMode]);

  // Wait for Google Maps to load
  useEffect(() => {
    if (!mapRef.current) return;
    
    if (!window.google || !window.google.maps) {
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps && mapRef.current) {
          clearInterval(checkGoogle);
          initializeMap();
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    }

    initializeMap();
  }, [initializeMap]);

  // Update map type when mode changes
  useEffect(() => {
    if (map) {
      map.setMapTypeId(mapMode === 'satellite' ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP);
    }
  }, [map, mapMode]);

  // Update markers when staff change
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    // Clear existing markers and info windows
    markers.forEach((marker) => marker.setMap(null));
    infoWindows.forEach(closeInfoWindow);

    const newMarkers: google.maps.Marker[] = [];
    const newInfoWindows: google.maps.InfoWindow[] = [];

    // Create markers for each staff member
    displayStaff.forEach((item) => {
      const { staff, location } = item;
      const position = new google.maps.LatLng(location.latitude, location.longitude);
      const isOnDuty = staff.spec?.isOnDuty === true;
      const booking = bookingMap.get(staff._id || '');

          const marker = new google.maps.Marker({
        position,
        map,
        icon: createStaffIcon(isOnDuty),
        title: staff.metadata?.name || 'Unknown',
      });

      // Build info content using string concatenation to avoid nested template literal issues
      const staffName = staff.metadata?.name || 'Unknown';
          const textColor = isDark ? '#f1f5f9' : '#0f172a';
          const bgColor = isDark ? '#1e293b' : '#ffffff';
          const secondaryColor = isDark ? '#cbd5e1' : '#475569';
      const roles = formatRoles(staff.spec?.roles || []) || 'N/A';
      const status = staff.spec?.status || 'N/A';
      const onDutyBadge = isOnDuty
        ? '<span style="margin-left: 8px; padding: 4px 8px; background-color: #ef4444; color: #FFFFFF; border-radius: 4px; font-size: 13px; font-weight: bold; display: inline-block;">ON DUTY</span>'
        : '';
      const bookingStatus = booking
        ? '<br /><strong style="color: ' + textColor + ';">Booking Status:</strong> ' + booking.status
        : '';
      const coordinates = location.latitude.toFixed(6) + ', ' + location.longitude.toFixed(6);

      const infoContent =
        '<div style="color: ' +
        textColor +
        '; background-color: ' +
        bgColor +
        '; font-size: 16px; min-width: 200px; padding: 12px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);">' +
        '<strong style="font-size: 18px; color: ' +
        textColor +
        ';">' +
        staffName +
        '</strong>' +
        onDutyBadge +
            '<br /><br />' +
        '<div style="color: ' +
        secondaryColor +
        '; margin-top: 8px;">' +
        '<strong style="color: ' +
        textColor +
        ';">Role:</strong> ' +
        roles +
        '<br />' +
        '<strong style="color: ' +
        textColor +
        ';">Status:</strong> ' +
        status +
        bookingStatus +
        '<br />' +
        '<strong style="color: ' +
        textColor +
        ';">Coordinates:</strong> ' +
        coordinates +
            '</div>' +
            '</div>';
          
          const infoWindow = new google.maps.InfoWindow({
            content: infoContent,
          });

          marker.addListener('click', () => {
        newInfoWindows.forEach(closeInfoWindow);
            infoWindow.open(map, marker);
          });

          newMarkers.push(marker);
      newInfoWindows.push(infoWindow);
    });

    setMarkers(newMarkers);
    setInfoWindows(newInfoWindows);

    // Fit bounds to show all markers
    if (newMarkers.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      newMarkers.forEach((marker) => {
        const position = marker.getPosition();
        if (position) bounds.extend(position);
      });
      map.fitBounds(bounds, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, displayStaff, isDark, bookingMap]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div
        ref={mapRef}
        style={{
          height: '100%',
          width: '100%',
          borderRadius: '6px',
          minHeight: '400px',
        }}
      />
      {/* Map Controls */}
        <div
          style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
            display: 'flex',
            flexDirection: 'column',
          gap: '8px',
        }}
      >
            <button
          onClick={() => setMapMode(mapMode === 'standard' ? 'satellite' : 'standard')}
              style={{
            padding: '8px 12px',
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
            border: `1px solid ${isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER}`,
            borderRadius: '6px',
            color: isDark ? '#f1f5f9' : '#0f172a',
                cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          {mapMode === 'standard' ? '🗺️ Satellite' : '🗺️ Map'}
            </button>
          </div>
    </div>
  );
};

export default StaffLiveTrackingTab;
